import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { Report } from './Report';
import strings from '../../../../translations';
import { motion } from 'motion/react';
import React, { useState } from 'react';

// Subgroup arrow behavior like LayerGroup.jsx (faAngleRight rotates 90°)
const reportsHeaderIconVariants = {
  open: { rotate: 90 },
  closed: { rotate: 0 }
};

const listVariants = {
  visible: { height: 'auto', opacity: 1 },
  hidden: { height: 0, opacity: 0 }
};

const StyledReportsGroups = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #f2f2f2;
  margin: 8px 0px 8px 0px;
  border-radius: 4px;
`;

const StyledReportsHeader = styled.div`
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  border-radius: 4px;
  padding: 8px 0px 8px 8px;
`;

const StyledReportsLeft = styled.div`
  display: flex;
  align-items: center;
`;

const StyledReportsName = styled.p`
  max-width: 220px;
  user-select: none;
  margin: 0;
  padding-right: 8px;
  font-size: 14px;
  font-weight: bold;
  color: ${(props) => props.theme.colors.mainColor1};

  @media ${(props) => props.theme.device.mobileL} {
    font-size: 12px;
  }
`;

const StyledReportsSelectButton = styled.button`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  margin-right: 8px;
  border: none;

  svg {
    color: ${(props) => props.theme.colors.mainColor1};
    font-size: 19px;
    transition: all 0.3s ease-out;
  }
`;

const StyledReportsContent = styled(motion.div)`
  margin: 0;
  padding-inline-start: 25px;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
`;

const StyledMotionIconWrapper = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const ReportGroup = React.memo(
  ({ reportsGroup, reportLinkGroups, lang }) => {
    const [isOpen, setIsOpen] = useState(false);

    // If no reports group or no links, render nothing.
    if (!reportsGroup || !reportLinkGroups?.length) return null;

    const toggleOpen = () => setIsOpen((prev) => !prev);

    const groupId = 'reports';

    // Prefer localized name if it exists, otherwise fallback to "Reports"
    const titleText =
      reportsGroup?.locale?.[lang]?.name ||
      reportsGroup?.locale?.[strings.getAvailableLanguages?.()?.[0] ?? lang]
        ?.name ||
      'Raportit';

    return (
      <StyledReportsGroups id={`group-${groupId}`}>
        <StyledReportsHeader
          id={`group-${groupId}-header`}
          role="button"
          tabIndex={0}
          aria-label={isOpen ? `Sulje ${titleText}` : `Avaa ${titleText}`}
          onClick={toggleOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter') toggleOpen();
          }}
        >
          <StyledReportsLeft id={`group-${groupId}-left`}>
            <StyledReportsSelectButton
              id={`group-${groupId}-toggle-left`}
              aria-label={isOpen ? `Sulje ${titleText}` : `Avaa ${titleText}`}
              type="button"
            >
              <StyledMotionIconWrapper
                initial="closed"
                animate={isOpen ? 'open' : 'closed'}
                variants={reportsHeaderIconVariants}
                transition={{ duration: 0.3, type: 'tween' }}
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </StyledMotionIconWrapper>
            </StyledReportsSelectButton>

            <StyledReportsName id={`group-${groupId}-name`}>
              {titleText}
            </StyledReportsName>
          </StyledReportsLeft>
        </StyledReportsHeader>

        <StyledReportsContent
          id={`group-${groupId}-content`}
          initial="hidden"
          animate={isOpen ? 'visible' : 'hidden'}
          variants={listVariants}
          transition={{ duration: 0.3, type: 'tween' }}
        >
          {/* Render report items (subgroups that have <url> in description) */}
          {reportLinkGroups.map((item, idx) => (
            <Report
              key={`report-link-${idx}`}
              isFirstSubgroup={true}
              index={item.index}
              link={item.link}
              group={item.group}
              lang={lang}
              style={{ margin: '8px' }}
            />
          ))}
        </StyledReportsContent>
      </StyledReportsGroups>
    );
  }
);
